import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  where,
  type CollectionReference,
} from "firebase/firestore";
import { firestore } from "@/helpers/firebase/firebase";
import { wordleGameDocumentConverter } from "@kekkle/shared/convertors/web/wordle-game-document.converter";
import { wordleGameStateDocumentConverter } from "@kekkle/shared/convertors/web/wordle-game-state-document.converter";
import type { WordleGameDocument } from "@kekkle/shared/types/documents/wordle-game-document/wordle-game-document";
import type { WordleGameStateDocument } from "@kekkle/shared/types/documents/wordle-game-state-document/wordle-game-state-document";
import { resetWordle } from "@/logic/wordleLogic";

/**
 * Returns a reference to the wordle games collection with converter
 */
function wordleGamesCollection(): CollectionReference<WordleGameDocument> {
  return collection(firestore, "wordle_games").withConverter(
    wordleGameDocumentConverter,
  );
}

/**
 * Returns a reference to the game states sub-collection with converter
 */
function gameStatesCollection(
  matchId: string,
): CollectionReference<WordleGameStateDocument> {
  return collection(
    firestore,
    "wordle_games",
    matchId,
    "game_states",
  ).withConverter(wordleGameStateDocumentConverter);
}

/**
 * Saves the game state for a user
 * @param matchId - Match/game ID
 * @param userUuid - User's UUID
 * @param payload - Game state payload
 */
export async function saveGameState(
  matchId: string,
  userUuid: string,
  payload: Omit<WordleGameStateDocument, "_id">,
): Promise<void> {
  if (!matchId || !userUuid) {
    throw new Error("MatchId or userUuid not set");
  }
  await setDoc(doc(gameStatesCollection(matchId), userUuid), payload);
}

/**
 * Fetches the game state for a user
 * @param matchId - Match/game ID
 * @param userUuid - User's UUID
 * @returns Game state or reset state if not found
 */
export async function fetchGameState(
  matchId: string,
  userUuid: string,
): Promise<any> {
  const stateDoc = await getDoc(doc(gameStatesCollection(matchId), userUuid));
  if (stateDoc.exists()) {
    const data = stateDoc.data();
    return JSON.parse(data.game_state);
  }
  return resetWordle();
}

/**
 * Fetches the newest game from Firestore
 * @param language - Language code (default: 'NL')
 * @returns Newest game document or null
 */
export async function fetchNewestGame(
  language: string = "NL",
): Promise<WordleGameDocument | null> {
  const matchQuery = query(
    wordleGamesCollection(),
    orderBy("date", "desc"),
    where("language", "==", language),
    limit(1),
  );

  const querySnapshot = await getDocs(matchQuery);
  if (querySnapshot.empty) {
    return null;
  }
  return querySnapshot.docs[0]?.data() ?? null;
}

/**
 * Fetches the game for a specific date
 * @param date - Date to fetch game for
 * @param language - Language code (default: 'NL')
 * @returns Game document or null if not found
 */
export async function fetchGamePerDate(
  date: Date,
  language: string = "NL",
): Promise<WordleGameDocument | null> {
  // Create a new Date object for the start of the day
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);

  // Create a new Date object for the end of the day
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);

  const matchQuery = query(
    wordleGamesCollection(),
    where("date", ">=", startOfDay),
    where("date", "<=", endOfDay),
    where("language", "==", language),
    orderBy("date", "desc"),
    limit(1),
  );

  const querySnapshot = await getDocs(matchQuery);
  if (querySnapshot.empty) {
    return null;
  }
  return querySnapshot.docs[0]?.data() ?? null;
}

/**
 * Return the id of the game for a specific date
 * @param date - Date to fetch game ID for
 * @param language - Language code (default: 'NL')
 * @returns Game ID or null if not found
 */
export async function fetchGameIdPerDate(
  date: Date,
  language: string = "NL",
): Promise<string | null> {
  const gameDoc = await fetchGamePerDate(date, language);
  return gameDoc?._id ?? null;
}
